import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Search } from 'lucide-react'
import { cx } from '@/lib/cx'
import type { Emoji, ReactionEmoji } from '@/types/api'
import { EmojiGrid } from './EmojiGrid'
import styles from './MediaPicker.module.css'

interface Props {
  /** Кастомные эмодзи: сначала текущего сервера, потом остальных. */
  custom: { guild: string; emojis: Emoji[] }[]
  onPick: (emoji: ReactionEmoji) => void
  onClose: () => void
}

/** Пикер эмодзи у сообщения: та же сетка, что во вкладке «Эмодзи», но без вкладок. */
export function EmojiPicker({ custom, onPick, onClose }: Props) {
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement | null>(null)
  const input = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    input.current?.focus()
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    // Esc — на window в фазе захвата: он раньше document, где Esc ловит модальное окно, и окно
    // эмодзи в модалке (название канала) закрывается само, не унося с собой модалку
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [onClose])

  return (
    <div
      className={cx(styles.picker, styles.compact, 'klichat-popover')}
      style={{ '--popover-origin': 'bottom right' } as CSSProperties}
      ref={ref}
      role="dialog"
      aria-label="Выбор эмодзи"
    >
      <div className={styles.search}>
        <Search size={16} className={styles.searchIcon} />
        <input ref={input} className={styles.input} placeholder="Поиск эмодзи" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Поиск эмодзи" />
      </div>
      <div className={cx(styles.body, 'scroller')}>
        <EmojiGrid custom={custom} query={query} onPick={onPick} />
      </div>
    </div>
  )
}
